import React, { useState, useEffect } from 'react';
import '../../styles/ServiceManagement.css';

const ServiceManagement = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });

  const [formData, setFormData] = useState({
    serviceName: '',
    description: '',
    durationMinutes: 60,
    price: '',
    category: '',
    bookingBufferMinutes: 15,
    maxConcurrentBookings: 1,
    staffMembers: [],
    bookingAdvanceDays: 30,
    isActive: true
  });

  const categories = [
    'Facial',
    'Massage',
    'Nails',
    'Hair',
    'Beauty',
    'Spa',
    'Consultation',
    'Other'
  ];

  // Helper function to get auth headers
  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      console.log('🔍 Fetching services...');
      const response = await fetch('/api/booking-v2/vendor/services', {
        headers: getAuthHeaders()
      });
      
      console.log('📡 Services response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('📊 Services data received:', data);
      
      setServices(data.services || []);
    } catch (error) {
      console.error('❌ Failed to fetch services:', error);
      showToast(`Failed to fetch services: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'info') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      console.log('💾 Submitting service data:', formData);
      
      let response;
      if (editingService) {
        response = await fetch(`/api/booking-v2/vendor/services/${editingService.id}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(formData)
        });
      } else {
        response = await fetch('/api/booking-v2/vendor/services', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(formData)
        });
      }

      console.log('📡 Submit response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Service operation successful:', result);

      showToast(editingService ? 'Service updated successfully' : 'Service created successfully', 'success');
      resetForm();
      fetchServices();
    } catch (error) {
      console.error('❌ Service operation failed:', error);
      showToast(error.message || 'Operation failed', 'error');
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      serviceName: service.service_name,
      description: service.description || '',
      durationMinutes: service.duration_minutes,
      price: service.price || '',
      category: service.category || '',
      bookingBufferMinutes: service.booking_buffer_minutes,
      maxConcurrentBookings: service.max_concurrent_bookings,
      staffMembers: service.staff_members || [],
      bookingAdvanceDays: service.booking_advance_days,
      isActive: service.is_active
    });
    setShowAddForm(true);
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    
    try {
      const response = await fetch(`/api/booking-v2/vendor/services/${serviceId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete service');
      }

      showToast('Service deleted successfully', 'success');
      fetchServices();
    } catch (error) {
      console.error('❌ Delete failed:', error);
      showToast(error.message, 'error');
    }
  };

  const handleToggleActive = async (service) => {
    try {
      const response = await fetch(`/api/booking-v2/vendor/services/${service.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          isActive: !service.is_active
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update service status');
      }

      showToast(`Service ${!service.is_active ? 'activated' : 'deactivated'}`, 'success');
      fetchServices();
    } catch (error) {
      console.error('❌ Toggle failed:', error);
      showToast('Failed to update service status', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      serviceName: '',
      description: '',
      durationMinutes: 60,
      price: '',
      category: '',
      bookingBufferMinutes: 15,
      maxConcurrentBookings: 1,
      staffMembers: [],
      bookingAdvanceDays: 30,
      isActive: true
    });
    setEditingService(null);
    setShowAddForm(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const addStaffMember = () => {
    const staffInput = document.getElementById('staffInput');
    const staffName = staffInput.value.trim();
    if (staffName && !formData.staffMembers.includes(staffName)) {
      setFormData(prev => ({
        ...prev,
        staffMembers: [...prev.staffMembers, staffName]
      }));
      staffInput.value = '';
    }
  };

  const removeStaffMember = (staffName) => {
    setFormData(prev => ({
      ...prev,
      staffMembers: prev.staffMembers.filter(name => name !== staffName)
    }));
  };

  if (loading) {
    return (
      <div className="service-management">
        <div className="loading-spinner-services">
          <div className="service-spinner"></div>
          <span>Loading services...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="service-management">
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          {toast.message}
        </div>
      )}

      <div className="service-header">
        <h2>Service Management</h2>
        <p className="service-header-subtitle">Create and manage your bookable services</p>
      </div>

      {/* Add Service Action */}
      {!showAddForm && (
        <div className="service-actions-container">
          <button 
            className="btn-primary"
            onClick={() => setShowAddForm(true)}
          >
            Add Service
          </button>
        </div>
      )}

      {/* Service List */}
      <div className="services-grid">
        {services.map(service => (
          <div key={service.id} className={`service-card ${!service.is_active ? 'inactive' : ''}`}>
            <div className="service-status">
              <span className={`service-status-badge ${service.is_active ? 'active' : 'inactive'}`}>
                {service.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            
            <div className="service-header-card">
              <h3>{service.service_name}</h3>
              <div className="service-actions">
                <button 
                  className="btn-secondary btn-sm"
                  onClick={() => handleEdit(service)}
                >
                  Edit
                </button>
                <button 
                  className={`btn-sm ${service.is_active ? 'btn-warning' : 'btn-success'}`}
                  onClick={() => handleToggleActive(service)}
                >
                  {service.is_active ? 'Deactivate' : 'Activate'}
                </button>
                <button 
                  className="btn-danger btn-sm"
                  onClick={() => handleDelete(service.id)}
                >
                  Delete
                </button>
              </div>
            </div>
            
            <div className="service-details">
              <p><strong>Category:</strong> {service.category}</p>
              <p><strong>Duration:</strong> {service.duration_minutes} minutes</p>
              {service.price && <p><strong>Price:</strong> R{service.price}</p>}
              <p><strong>Buffer:</strong> {service.booking_buffer_minutes} minutes</p>
              <p><strong>Max Concurrent:</strong> {service.max_concurrent_bookings}</p>
              {service.description && <p><strong>Description:</strong> {service.description}</p>}
              {service.staff_members && service.staff_members.length > 0 && (
                <p><strong>Staff:</strong> {service.staff_members.join(', ')}</p>
              )}
              <p><strong>Bookings:</strong> {service.booking_count || 0}</p>
            </div>
          </div>
        ))}
      </div>

      {services.length === 0 && (
        <div className="no-services">
          <h3>No services configured yet</h3>
          <p>Create your first service to start accepting bookings from customers</p>
          <button 
            className="btn-primary"
            onClick={() => setShowAddForm(true)}
          >
            Add Your First Service
          </button>
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showAddForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingService ? 'Edit Service' : 'Add New Service'}</h3>
              <button className="modal-close" onClick={resetForm}>×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="service-form">
              <div className="form-grid">
                <div className="form-group">
                  <label>Service Name *</label>
                  <input
                    type="text"
                    name="serviceName"
                    value={formData.serviceName}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Category</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat.toLowerCase()}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Duration (minutes) *</label>
                  <input
                    type="number"
                    name="durationMinutes"
                    value={formData.durationMinutes}
                    onChange={handleInputChange}
                    min="15"
                    max="480"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Price (R)</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                  />
                </div>

                <div className="form-group">
                  <label>Buffer Time (minutes)</label>
                  <input
                    type="number"
                    name="bookingBufferMinutes"
                    value={formData.bookingBufferMinutes}
                    onChange={handleInputChange}
                    min="0"
                    max="60"
                  />
                </div>

                <div className="form-group">
                  <label>Max Concurrent Bookings</label>
                  <input
                    type="number"
                    name="maxConcurrentBookings"
                    value={formData.maxConcurrentBookings}
                    onChange={handleInputChange}
                    min="1"
                    max="10"
                  />
                </div>

                <div className="form-group">
                  <label>Booking Advance Days</label>
                  <input
                    type="number"
                    name="bookingAdvanceDays"
                    value={formData.bookingAdvanceDays}
                    onChange={handleInputChange}
                    min="1"
                    max="365"
                  />
                </div>

                <div className="form-group form-group-checkbox">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  <label>Active Service</label>
                </div>
              </div>

              <div className="form-group full-width">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>

              <div className="form-group full-width">
                <label>Staff Members</label>
                <div className="staff-input-group">
                  <input
                    type="text"
                    id="staffInput"
                    placeholder="Enter staff member name"
                  />
                  <button type="button" onClick={addStaffMember}>Add</button>
                </div>
                <div className="staff-list">
                  {formData.staffMembers.map(staff => (
                    <span key={staff} className="staff-tag">
                      {staff}
                      <button 
                        type="button" 
                        onClick={() => removeStaffMember(staff)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={resetForm}>Cancel</button>
                <button type="submit" className="btn-primary">
                  {editingService ? 'Update Service' : 'Create Service'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceManagement;